import type { TOC, TOCChild, TOCParent } from '~/common/types';

interface SpecialDatasetConfig {
  name: string;
  toc: TOC;
}

const isTOCParent = (item: TOCParent | TOCChild): item is TOCParent => Boolean((item as TOCParent).children);

// Registry keys plus every walked leaf id.
function computeLayerIds(config: Record<string, SpecialDatasetConfig>): Set<string> {
  const ids = new Set<string>(Object.keys(config));
  const walk = (items: TOC) => {
    items.forEach((item) => {
      if (isTOCParent(item)) walk(item.children);
      else ids.add(item.id);
    });
  };
  Object.values(config).forEach((entry) => {
    walk(entry.toc);
  });
  return ids;
}

function computeNames(config: Record<string, SpecialDatasetConfig>): Set<string> {
  return new Set(Object.values(config).map(({ name }) => name));
}

// Empty until the org's DB bundle arrives; refilled in place so call-time consumers keep the same bindings.
const datasetConfig: Record<string, SpecialDatasetConfig> = {};
const allSpecialDatasetLayerIds = new Set<string>();
const allSpecialDatasetNames = new Set<string>();

function fill(next: Record<string, SpecialDatasetConfig>): void {
  for (const key of Object.keys(datasetConfig)) delete datasetConfig[key];
  for (const [key, value] of Object.entries(next)) datasetConfig[key] = value;
  allSpecialDatasetLayerIds.clear();
  computeLayerIds(next).forEach((id) => {
    allSpecialDatasetLayerIds.add(id);
  });
  allSpecialDatasetNames.clear();
  computeNames(next).forEach((name) => {
    allSpecialDatasetNames.add(name);
  });
}

const getSpecialDatasetConfig = (datasetId: string) => datasetConfig[datasetId];

function applyDbSpecialDatasetsBundle(bundle: { datasetConfig: Record<string, SpecialDatasetConfig> }): void {
  fill(structuredClone(bundle.datasetConfig));
}

/** Empty the active singletons; must run on every org switch without a bundle so a previous org's datasets can't leak. */
function clearSpecialDatasets(): void {
  fill({});
}

export {
  allSpecialDatasetLayerIds,
  allSpecialDatasetNames,
  applyDbSpecialDatasetsBundle,
  clearSpecialDatasets,
  datasetConfig,
  getSpecialDatasetConfig,
  type SpecialDatasetConfig,
};
